// PLAN-cockpit-v2 Phase 0-F: ⌘K Command Palette (基本機能)
//
// 起動: Cmd+K / Ctrl+K
// 候補: Search query / Lens 切替 / Logout
// 後続 (Phase 1+): +New entry / +New article / Settings 等

import { useEffect, useMemo, useRef, useState } from 'react'
import { useV2Router, type LensId, LENS_ORDER } from '../router'
import { useTheme } from '../theme'

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
  onLogout: () => void
}

interface Cmd {
  id: string
  label: string
  hint?: string
  run: () => void
}

const LENS_LABEL: Record<LensId, string> = {
  home: 'Home',
  explore: 'Explore',
  reflect: 'Reflect',
  judgment: 'Judgment',
  finance: 'Finance',
  health: 'Health',
  mind: 'Mind',
  collaborate: 'Collaborate',
  graph: 'Graph',
}

export function CommandPalette({ open, onClose, onLogout }: CommandPaletteProps) {
  const { navigate } = useV2Router()
  const { theme, toggle: toggleTheme } = useTheme()
  const [query, setQuery] = useState('')
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setQuery('')
      setActiveIdx(0)
      // focus 直後に
      const t = setTimeout(() => inputRef.current?.focus(), 50)
      return () => clearTimeout(t)
    }
  }, [open])

  const baseCommands = useMemo<Cmd[]>(() => {
    const lensCmds: Cmd[] = LENS_ORDER.map((l, i) => ({
      id: `lens-${l}`,
      label: `Go to ${LENS_LABEL[l]}`,
      hint: `Cmd+${i}`,
      run: () => {
        navigate(l)
        onClose()
      },
    }))
    const others: Cmd[] = [
      {
        id: 'theme-toggle',
        label: `Switch theme (${theme === 'light' ? 'Light → Dark' : 'Dark → Light'})`,
        run: () => {
          toggleTheme()
          onClose()
        },
      },
      {
        id: 'logout',
        label: 'Logout',
        run: () => {
          onLogout()
          onClose()
        },
      },
    ]
    return [...lensCmds, ...others]
  }, [navigate, onClose, theme, toggleTheme, onLogout])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return baseCommands
    return baseCommands.filter((c) => c.label.toLowerCase().includes(q))
  }, [baseCommands, query])

  if (!open) return null

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(0, i - 1))
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      filtered[activeIdx]?.run()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4 bg-bg-base/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-bg-surface border border-border-strong rounded-lg shadow-overlay overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          placeholder="Search commands or pages…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setActiveIdx(0)
          }}
          onKeyDown={onKeyDown}
          className="w-full px-5 py-4 text-base bg-transparent border-b border-border-subtle text-text-base placeholder:text-text-subtle focus:outline-none"
        />
        <ul role="listbox" className="max-h-[50vh] overflow-y-auto">
          {filtered.length === 0 ? (
            <li className="px-5 py-4 text-sm text-text-subtle">No matching commands</li>
          ) : (
            filtered.map((c, i) => (
              <li key={c.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === activeIdx}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => c.run()}
                  className={`
                    w-full text-left px-5 py-3 flex items-center justify-between gap-4 text-sm
                    ${i === activeIdx ? 'bg-bg-overlay text-text-base' : 'text-text-base hover:bg-bg-overlay'}
                  `}
                >
                  <span>{c.label}</span>
                  {c.hint && <span className="text-xs text-text-subtle font-mono">{c.hint}</span>}
                </button>
              </li>
            ))
          )}
        </ul>
        <footer className="px-5 py-2 border-t border-border-subtle text-[11px] text-text-subtle flex justify-between">
          <span>↑↓ navigate · Enter select · Esc close</span>
          <span>⌘K</span>
        </footer>
      </div>
    </div>
  )
}
